import { getSnippetNameFromString } from 'modules/dynamicContentBuilder/utils';
import { useSnippetByName } from 'modules/snippets/hooks/useSnippetByName';
import styled from 'styled-components';
import { EmptyAppState } from 'ui/illustrations';
import { Skeleton } from 'ui/skeleton';
import { isNull } from 'utils/tsHelpers';
import { useTranslation } from '../../../hooks/useTranslation';
import { useContentWatch } from '../../../hooks/form';

const Wrapper = styled.div`
  gap: ${({ theme }) => theme.spacing.values.half};
  grid-area: center;
`;

const SnippetReferencePreview = styled.div`
  font-family: Menlo, Monaco, Consolas, 'Lucida Console', monospace;
  color: ${({ theme }) => theme.colors.primaryColors.cyan100};
  margin-bottom: ${({ theme }) => theme.spacing.values.half};
`;

const SnippetContentsPreview = styled.div`
  background-color: #f5f6f6;
  border-radius: 3px;
  padding: ${({ theme }) => theme.spacing.values.half};
  font-family: Menlo, Monaco, Consolas, 'Lucida Console', monospace;
  color: ${({ theme }) => theme.colors.textColors.textPrimary};
`;

const StyledSkeleton = styled(Skeleton)`
  border-radius: 3px;
  background-color: #f5f6f6;
  height: ${({ theme }) => theme.spacing.values.x3};
`;

const SnippetLoadError = styled.div`
  background-color: #f5f6f6;
  border-radius: 3px;
  padding: ${({ theme }) => theme.spacing.values.half};
  text-align: center;
  padding-bottom: ${({ theme }) => theme.spacing.values.double};
`;

const ErrorTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textColors.textPrimary};
`;

const ErrorDescription = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textColors.textSecondary};
`;

export const SnippetPreview = () => {
  const content = useContentWatch() ?? '';
  const snippetName = getSnippetNameFromString(content);
  const { snippet, isLoading, didError } = useSnippetByName(snippetName);
  const { t } = useTranslation();

  const shouldShowErrorMessage = !isLoading && (didError || isNull(snippet));
  const errorMessage = shouldShowErrorMessage && (
    <SnippetLoadError data-test="snippet-preview--error">
      <EmptyAppState />
      <ErrorTitle>{t('contentPicker.snippetLoadingErrorTitle')}</ErrorTitle>
      <ErrorDescription>{t('contentPicker.snippetLoadingErrorDescription')}</ErrorDescription>
    </SnippetLoadError>
  );

  const loadingSkeleton = isLoading && <StyledSkeleton />;

  const shouldShowPreview = !isLoading && !didError && !isNull(snippet);
  const preview = shouldShowPreview && (
    <SnippetContentsPreview data-test="snippet-preview--content">
      {snippet.content}
    </SnippetContentsPreview>
  );

  return (
    <Wrapper data-test="snippet-preview">
      <SnippetReferencePreview>{content}</SnippetReferencePreview>
      {errorMessage}
      {loadingSkeleton}
      {preview}
    </Wrapper>
  );
};
